/**
 * Farm & Acre Co - Print Line Inputs
 *
 * Handles the Allflex print program selector:
 * - Dropdown shows programs (from metaobjects) with line count + fee
 * - Selecting a program shows preview image, correct line inputs with char limits
 * - Updates hidden fee-compatible input for fee-auto-add.js
 *
 * Supports:
 * - Single print section (visual_tag template): print-style
 * - Dual print sections (nlis_cattle template): print-style-female + print-style-male
 * - Sides-based printing (sheep/goat/feedlot): print-sides (unchanged)
 */

(function() {
  'use strict';

  // === DEBUG SYSTEM ===
  const FEE_DEBUG = false; // Set to false to disable debug logging
  const DEBUG_PREFIX = '[PRINT INPUTS DEBUG]';

  function debugLog(message, data = null) {
    if (FEE_DEBUG) {
      if (data !== null) {
        console.log(DEBUG_PREFIX, message, data);
      } else {
        console.log(DEBUG_PREFIX, message);
      }
    }
  }

  function debugError(message, error = null) {
    if (FEE_DEBUG) {
      if (error !== null) {
        console.error(DEBUG_PREFIX, message, error);
      } else {
        console.error(DEBUG_PREFIX, message);
      }
    }
  }

  // Store loaded program data
  let programs = { female: [], male: [], single: [] };
  let selectedPrograms = { female: null, male: null, single: null };

  debugLog('=== PRINT LINE INPUTS INITIALIZING ===');

  function initPrintLineInputs() {
    debugLog('=== INITIALIZING PRINT LINE INPUTS ===');
    
    // Load print programs from embedded JSON
    loadPrintPrograms();

    // Print sides (sheep/goat/feedlot simplified print options)
    initPrintSides();

    // Single print style (visual_tag template)
    const singleStyleSelect = document.querySelector('[data-allflex-field="print-style"]');
    debugLog('Single print style select found:', !!singleStyleSelect);

    // Dual print styles (nlis_cattle template)
    const femaleStyleSelect = document.querySelector('[data-allflex-field="print-style-female"]');
    const maleStyleSelect = document.querySelector('[data-allflex-field="print-style-male"]');
    debugLog('Female print style select found:', !!femaleStyleSelect);
    debugLog('Male print style select found:', !!maleStyleSelect);

    // Initialize single print section
    if (singleStyleSelect) {
      initPrintStyleSection(singleStyleSelect, {
        containerId: 'print-line-inputs',
        linePrefix: 'print-line',
        inputPrefix: 'allflex-line',
        customId: 'print-line-custom',
        previewId: 'print-style-preview',
        previewImgId: 'print-style-preview-img',
        hiddenFeeId: 'allflex-print-lines-hidden',
        side: 'single'
      });
    }

    // Initialize female print section
    if (femaleStyleSelect) {
      initPrintStyleSection(femaleStyleSelect, {
        containerId: 'female-print-line-inputs',
        linePrefix: 'female-print-line',
        inputPrefix: 'female-line',
        customId: 'female-print-line-custom',
        previewId: 'print-style-preview-female',
        previewImgId: 'print-style-preview-img-female',
        hiddenFeeId: 'female-print-lines-hidden',
        side: 'female'
      });
    }

    // Initialize male print section
    if (maleStyleSelect) {
      initPrintStyleSection(maleStyleSelect, {
        containerId: 'male-print-line-inputs',
        linePrefix: 'male-print-line',
        inputPrefix: 'male-line',
        customId: 'male-print-line-custom',
        previewId: 'print-style-preview-male',
        previewImgId: 'print-style-preview-img-male',
        hiddenFeeId: 'male-print-lines-hidden',
        side: 'male'
      });
    }

    // Also support legacy selects (fallback if no programs loaded)
    const legacySingleSelect = document.querySelector('[data-allflex-field="print-lines"]');
    debugLog('Legacy print lines select found:', !!legacySingleSelect, legacySingleSelect);
    if (legacySingleSelect && legacySingleSelect.tagName === 'SELECT') {
      debugLog('Initializing legacy print section');
      initLegacyPrintSection(legacySingleSelect, 'print-line-inputs', {
        1: 'print-line-1',
        2: 'print-line-2',
        3: 'print-line-3',
        custom: 'print-line-custom'
      }, 'single');
    }

    // Initialize character counters on all line inputs
    document.querySelectorAll('[data-allflex-field*="print-line"]').forEach(input => {
      if (input.tagName === 'INPUT' && input.type === 'text') {
        input.addEventListener('input', function() {
          updateCharCounter(this);
        });
      }
    });

    debugLog('=== PRINT LINE INPUTS INITIALIZATION COMPLETE ===');
  }

  /**
   * Load print programs from embedded JSON in the page
   */
  function loadPrintPrograms() {
    const femaleData = document.getElementById('PrintProgramsDataFemale');
    if (femaleData) {
      try {
        programs.female = JSON.parse(femaleData.textContent);
        // console.log('Loaded', programs.female.length, 'female print programs');
      } catch (e) {
        console.warn('Failed to parse female print programs:', e);
      }
    }

    const maleData = document.getElementById('PrintProgramsDataMale');
    if (maleData) {
      try {
        programs.male = JSON.parse(maleData.textContent);
        // console.log('Loaded', programs.male.length, 'male print programs');
      } catch (e) {
        console.warn('Failed to parse male print programs:', e);
      }
    }

    const singleData = document.getElementById('PrintProgramsData');
    if (singleData) {
      try {
        const parsed = JSON.parse(singleData.textContent);
        if (Array.isArray(parsed)) {
          programs.single = parsed;
          // console.log('Loaded', programs.single.length, 'print programs');
        }
      } catch (e) {
        console.warn('Failed to parse print programs:', e);
      }
    }
  }

  /**
   * Find a program by its program_id
   */
  function findProgramById(programId, side) {
    const programList = side === 'single'
      ? (programs.single.length ? programs.single : programs.female)
      : programs[side];
    if (!programList || !programList.length) return null;
    return programList.find(p => p.program_id === programId) || null;
  }

  /**
   * Find program by line count (legacy fallback)
   */
  function findProgramByLineCount(lineCount, side) {
    const programList = side === 'single'
      ? (programs.single.length ? programs.single : programs.female)
      : programs[side];
    if (!programList || !programList.length) return null;
    const matching = programList.filter(p => {
      const pLineCount = p.line_count || (p.lines && p.lines.length) || 0;
      return pLineCount === lineCount;
    });
    return matching.length > 0 ? matching[0] : null;
  }

  /**
   * Safely get parsed lines array from a program
   */
  function getProgramLines(program) {
    if (!program || !program.lines) return [];
    let lines = program.lines;
    if (typeof lines === 'string') {
      try { lines = JSON.parse(lines); } catch (e) { return []; }
    }
    return Array.isArray(lines) ? lines : [];
  }

  /**
   * Get character limits from a program's line specs.
   * Indexes by the `line` property (1..5), not by array position. Some metaobject
   * records have line-data gaps (e.g. male IXB000000 returns [line:2, line:3,
   * line:4] with line 1 missing); iterating by position would offset all limits.
   */
  function getCharLimitsFromProgram(program) {
    const lines = getProgramLines(program);
    const lineCount = program?.line_count || lines.length || 0;
    if (lines.length === 0) return [12, 12, 12, 12, 12];
    const byLine = {};
    lines.forEach(l => { if (l && l.line) byLine[l.line] = l; });
    const limits = [];
    for (let i = 1; i <= Math.max(lineCount, 5); i++) {
      limits.push(byLine[i]?.max_chars || 12);
    }
    return limits;
  }

  /**
   * Initialize a print style section (program-based dropdown)
   */
  function initPrintStyleSection(selectElement, config) {
    const container = document.getElementById(config.containerId);
    const previewContainer = document.getElementById(config.previewId);
    const previewImg = document.getElementById(config.previewImgId);
    const hiddenFeeInput = document.getElementById(config.hiddenFeeId);
    // The layout row wraps preview + line inputs for side-by-side layout
    const layoutRow = container ? container.closest('.print-layout-row') : null;

    if (!container) return;

    // Build line input references (up to 5)
    const lineInputs = {};
    for (let i = 1; i <= 5; i++) {
      const el = document.getElementById(config.linePrefix + '-' + i);
      if (el) lineInputs[i] = el;
    }
    const customEl = document.getElementById(config.customId);
    if (customEl) lineInputs.custom = customEl;

    selectElement.addEventListener('change', function() {
      const selectedOption = this.options[this.selectedIndex];
      const programId = this.value;
      const lineCount = parseInt(selectedOption.dataset.lineCount) || 0;
      const feeValue = selectedOption.dataset.feeValue || 'blank';
      const previewUrl = selectedOption.dataset.previewImage || '';

      // Update hidden fee input
      if (hiddenFeeInput) {
        hiddenFeeInput.value = feeValue;
        hiddenFeeInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update preview image
      if (previewContainer && previewImg) {
        if (previewUrl && programId !== 'blank' && programId !== 'custom') {
          previewImg.src = previewUrl;
          previewImg.alt = 'Print layout preview - ' + selectedOption.text.trim();
          previewContainer.style.display = '';
        } else {
          previewContainer.style.display = 'none';
          previewImg.src = '';
        }
      }

      // Find program for char limits
      let program = null;
      if (programId !== 'blank' && programId !== 'custom') {
        program = findProgramById(programId, config.side);
      }
      selectedPrograms[config.side] = program;

      // Update visible lines
      updateVisibleLinesForProgram(programId, lineCount, program, lineInputs, layoutRow, previewContainer);

      // Dispatch event for dynamic pricing
      document.dispatchEvent(new CustomEvent('printLinesChanged', {
        detail: {
          selection: feeValue,
          side: config.side,
          program: program,
          lineCount: lineCount
        }
      }));
    });

    // Trigger initial state
    selectElement.dispatchEvent(new Event('change'));
  }

  /**
   * Update visible line inputs based on selected program
   */
  function updateVisibleLinesForProgram(programId, lineCount, program, lineInputs, layoutRow, previewContainer) {
    // Hide all line inputs
    Object.values(lineInputs).forEach(el => {
      if (el) {
        el.style.display = 'none';
        const input = el.querySelector('input, textarea');
        if (input) {
          input.value = '';
          input.removeAttribute('required');
          updateCharCounter(input);
        }
      }
    });

    if (programId === 'blank') {
      // Hide the whole layout row
      if (layoutRow) layoutRow.style.display = 'none';
      return;
    }

    // Show the layout row
    if (layoutRow) layoutRow.style.display = '';

    if (programId === 'custom') {
      // Hide preview for custom, show custom textarea
      if (previewContainer) previewContainer.style.display = 'none';
      if (lineInputs.custom) {
        lineInputs.custom.style.display = 'block';
        const textarea = lineInputs.custom.querySelector('textarea');
        if (textarea) textarea.setAttribute('required', 'required');
      }
      return;
    }

    // Show the right number of line inputs. Index programLines by the `line`
    // property (1..5), not by array position — some metaobjects have line gaps
    // (male IXB000000 returns lines 2,3,4 only) and positional indexing would
    // offset every spec by one.
    const charLimits = getCharLimitsFromProgram(program);
    const programLines = getProgramLines(program);
    const linesByNum = {};
    programLines.forEach(l => { if (l && l.line) linesByNum[l.line] = l; });

    for (let i = 1; i <= lineCount; i++) {
      if (lineInputs[i]) {
        showLineInput(lineInputs[i], charLimits[i - 1], linesByNum[i]);
      }
    }
  }

  /**
   * Legacy: initialize old-style print section (hardcoded dropdown)
   * Only used as fallback when no programs are available
   */
  function initLegacyPrintSection(selectElement, containerId, lineIds, side) {
    const container = document.getElementById(containerId);
    if (!container) return;

    const lineInputs = {};
    Object.keys(lineIds).forEach(key => {
      lineInputs[key] = document.getElementById(lineIds[key]);
    });

    selectElement.addEventListener('change', function() {
      const selection = this.value;
      const lineCount = parseLineCount(selection);

      if (lineCount > 0 && lineCount <= 3) {
        const program = findProgramByLineCount(lineCount, side);
        selectedPrograms[side] = program;
      } else {
        selectedPrograms[side] = null;
      }

      updateVisibleLines(selection, container, lineInputs, side);
    });

    updateVisibleLines(selectElement.value, container, lineInputs, side);
  }

  function parseLineCount(selection) {
    switch (selection) {
      case '1-line': return 1;
      case '2-lines': return 2;
      case '3-lines': return 3;
      case 'custom': return 4;
      default: return 0;
    }
  }

  function updateVisibleLines(selection, container, lineInputs, side) {
    container.style.display = 'none';

    Object.values(lineInputs).forEach(el => {
      if (el) {
        el.style.display = 'none';
        const input = el.querySelector('input, textarea');
        if (input) {
          input.value = '';
          input.removeAttribute('required');
          updateCharCounter(input);
        }
      }
    });

    const program = selectedPrograms[side];
    const charLimits = getCharLimitsFromProgram(program);
    const programLines = getProgramLines(program);

    switch (selection) {
      case 'blank':
        break;
      case '1-line':
        container.style.display = 'block';
        if (lineInputs[1]) showLineInput(lineInputs[1], charLimits[0], programLines[0]);
        break;
      case '2-lines':
        container.style.display = 'block';
        [1, 2].forEach((n, idx) => {
          if (lineInputs[n]) showLineInput(lineInputs[n], charLimits[idx], programLines[idx]);
        });
        break;
      case '3-lines':
        container.style.display = 'block';
        [1, 2, 3].forEach((n, idx) => {
          if (lineInputs[n]) showLineInput(lineInputs[n], charLimits[idx], programLines[idx]);
        });
        break;
      case 'custom':
        container.style.display = 'block';
        if (lineInputs.custom) {
          lineInputs.custom.style.display = 'block';
          const textarea = lineInputs.custom.querySelector('textarea');
          if (textarea) textarea.setAttribute('required', 'required');
        }
        break;
    }

    document.dispatchEvent(new CustomEvent('printLinesChanged', {
      detail: { selection, side, program }
    }));
  }

  /**
   * Show a line input with dynamic character limit
   */
  function showLineInput(lineElement, maxChars, lineSpec) {
    if (!lineElement) return;

    lineElement.style.display = 'block';

    const input = lineElement.querySelector('input');
    if (input) {
      input.maxLength = maxChars;
      input.setAttribute('required', 'required');
      input.placeholder = `Max ${maxChars} characters`;
      // Stash the laser/ink + mm spec so the order summary can render a readable
      // "Line 1 (19mm laser): ABC" instead of the cryptic Allflex program ID.
      if (lineSpec && lineSpec.font_size) {
        input.dataset.spec = lineSpec.font_size + 'mm ' + (lineSpec.font_type || 'print');
      } else {
        delete input.dataset.spec;
      }
      updateCharCounter(input);
    }

    // Update label with line spec info
    const label = lineElement.querySelector('.allflex-field__label');
    if (label && lineSpec) {
      const lineNum = lineElement.dataset.line || lineElement.id.match(/\d/)?.[0] || '?';
      const specText = lineSpec.font_type
        ? ` <span class="print-line-spec">(${lineSpec.font_size}mm ${lineSpec.font_type})</span>`
        : '';
      label.innerHTML = `Line ${lineNum}${specText} <span class="print-line-char-counter">(0/${maxChars})</span>`;
    }
  }

  function updateCharCounter(input) {
    if (!input || input.tagName !== 'INPUT') return;

    const lineElement = input.closest('.print-line-input');
    const counter = lineElement?.querySelector('.print-line-char-counter') ||
                    input.parentElement.querySelector('.print-line-char-counter');

    if (counter) {
      const current = input.value.length;
      const max = input.maxLength || 12;
      counter.textContent = `(${current}/${max})`;

      counter.classList.remove('at-limit', 'near-limit');
      if (current >= max) {
        counter.classList.add('at-limit');
      } else if (current >= max * 0.8) {
        counter.classList.add('near-limit');
      }
    }
  }

  /**
   * Initialize info popup toggles for print layout reference (Issue 3)
   * Uses native details/summary with JS to toggle sibling content visibility
   */
  function initInfoPopups() {
    debugLog('=== INITIALIZING INFO POPUPS ===');

    const infoDetails = document.querySelectorAll('.allflex-field__info-popup');
    debugLog('Info popup details found:', infoDetails.length);

    infoDetails.forEach(details => {
      const field = details.closest('.allflex-field');
      const content = field?.querySelector('.allflex-field__info-content');

      if (!content) {
        debugLog('No content found for this info popup');
        return;
      }

      // Listen for toggle event
      details.addEventListener('toggle', function() {
        if (details.open) {
          content.style.display = 'block';
        } else {
          content.style.display = 'none';
        }
      });

      debugLog('Info popup listener added');
    });

    debugLog('=== INFO POPUPS INITIALIZATION COMPLETE ===');
  }

  /**
   * Initialize print sides dropdown (sheep/goat/feedlot tags)
   */
  function initPrintSides() {
    debugLog('=== INITIALIZING PRINT SIDES ===');
    
    const sidesSelect = document.querySelector('[data-allflex-field="print-sides"]');
    debugLog('Sides dropdown element:', sidesSelect);
    debugLog('Sides dropdown found:', !!sidesSelect);
    
    if (!sidesSelect) {
      debugLog('No sides dropdown found - this product does not use sides-based printing');
      return;
    }

    const textContainer = sidesSelect.closest('.print-options-section')?.querySelector('.print-sides-inputs')
      || document.getElementById('print-sides-inputs');
    
    debugLog('Text container element:', textContainer);
    debugLog('Text container found:', !!textContainer);

    if (!textContainer) {
      debugError('!!! TEXT CONTAINER NOT FOUND FOR SIDES !!!');
      return;
    }

    sidesSelect.addEventListener('change', function() {
      const val = this.value;
      debugLog('=== SIDES DROPDOWN CHANGED ===');
      debugLog('Selected value:', val);
      debugLog('Selected text:', this.options[this.selectedIndex]?.text);

      // SPACEY: Update hidden input for fee compatibility (matches print-lines pattern)
      const hiddenFeeInput = document.getElementById('print-sides-hidden');
      if (hiddenFeeInput) {
        hiddenFeeInput.value = val;
        hiddenFeeInput.dispatchEvent(new Event('change', { bubbles: true }));
        debugLog('Updated hidden fee input:', val);
      }

      if (val === 'Printed 1 Side' || val === 'Printed 2 Sides') {
        debugLog('Showing text container for printing');
        textContainer.style.display = 'block';
      } else {
        debugLog('Hiding text container (No Print)');
        textContainer.style.display = 'none';
        const textarea = textContainer.querySelector('textarea');
        if (textarea) textarea.value = '';
      }

      debugLog('Current dropdown value after change:', this.value);
      debugLog('Dropdown value attribute:', this.value);
    });

    sidesSelect.dispatchEvent(new Event('change'));
    debugLog('=== PRINT SIDES INITIALIZATION COMPLETE ===');
  }

  // SPACEY: Initialize info popup toggles for print layout reference (Issue 3)
  initInfoPopups();

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPrintLineInputs);
  } else {
    initPrintLineInputs();
  }

  // Export for external use
  window.printLineInputs = {
    getPrograms: () => programs,
    getSelectedPrograms: () => selectedPrograms,
    findProgramById,
    findProgramByLineCount
  };

})();
